/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Open Source Version is distributed with Sencha Ext JS pursuant to a FLOSS Exception agreed upon
 * between Sonatype, Inc. and Sencha Inc. Sencha Ext JS is licensed under GPL v3 and cannot be redistributed as part of a
 * closed source work.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
/**
 * Preview Browse Routes - SUPER UX Migration
 *
 * Search uses the unified search page for all formats.
 */

import {UIView} from '@uirouter/react';
import {
  Permissions,
  // Preview UI page components (pre-lazied in nexus-ui-plugin)
  HealthReportPage,
  FirewallReportPage,
  WelcomeSuper,
  MalwareRemediationWizard,
  MaliciousPackagesPage,
  MalwareRiskPageSuper,
  BrowsePage,
  AuditLogPage,
  TagsPage,
  TagDetailPage,
  UploadPage,
  UploadFormPage,
  ApiPage,
  FirewallSecurityEvaluationsPage,
  FirewallSecurityMalwarePage,
  FirewallSecurityPoliciesPage,
  FirewallSecurityQuarantinePage,
  FirewallSecurityQuarantineDetailPage,
  FirewallSecurityContainerDetailPage,
  FirewallSecurityContainerComponentDetailPage,
  FirewallSecurityQuarantineViolationPage,
  FirewallSecurityWaiversPage,
  FirewallSecurityWaiverDetailPage,
  FirewallSecurityWaiverRenewPage,
  RemediatePage,
  GADetailPage,
  CustomSearchPage,
  UnifiedSearchPage,
  TrashPage as TrashPageRaw,
  withFeatureGate,
  PRO_FAMILY_EDITIONS,
} from '@sonatype/nexus-ui-plugin';

// Note: AuditLogPage is not available in cloud — the preview.browse.audit
// route is omitted from cloudui's route array below.

const TrashPage = withFeatureGate(TrashPageRaw, 'system.trash', 'Trash');

export const previewBrowseRoutes = [
  // =============================================================================
  // ROOT ROUTES
  // =============================================================================
  {
    name: 'preview',
    url: 'preview',
    abstract: true,
    component: UIView,
  },
  {
    name: 'preview.browse',
    url: '/browse', // Nested under preview: #/preview/browse
    abstract: true,
    component: UIView,
    data: {title: 'Browse'},
  },

  // =============================================================================
  // DASHBOARD
  // =============================================================================
  {
    name: 'preview.browse.welcome',
    url: '/welcome?tab', // Nested: #/preview/browse/welcome
    component: WelcomeSuper,
    params: {
      tab: { type: 'string', dynamic: true, value: 'overview' },
    },
    data: {
      title: 'Welcome',
      visibilityRequirements: {
        anonymousAccessOrHasUser: true,
      },
    },
  },

  // =============================================================================
  // PROTECT — Malware Risk Hub (Overview, Detect, Remediate, Harden)
  // Wizard: sibling route so /protect?tab can render without blocking /protect/wizard/...
  // =============================================================================
  {
    name: 'preview.browse.protect',
    url: '/protect?tab',
    component: MaliciousPackagesPage,
    params: {
      tab: {value: 'overview', type: 'string', dynamic: true},
    },
    data: {
      title: 'Protect',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },
  {
    name: 'preview.browse.protectWizard',
    url: '/protect/wizard/:repositoryName?step',
    component: MalwareRemediationWizard,
    params: {
      repositoryName: {type: 'string', dynamic: true},
      step: {type: 'string', value: '1', dynamic: true},
    },
    data: {
      title: 'Malicious Packages Wizard',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },

  // =============================================================================
  // API DOCUMENTATION - Swagger UI (promoted from Settings, no sidebar)
  // Requires authentication to view
  // =============================================================================
  {
    name: 'preview.browse.api',
    url: '/api',
    component: ApiPage,
    data: {
      title: 'API',
      visibilityRequirements: {
        requiresUser: true,
        permissions: [Permissions.SETTINGS.READ],
      },
    },
  },

  // =============================================================================
  // SECURITY - Firewall Security section (one route per page, one nav item per route)
  // Each page renders MissingRoutePage when the Security pages are not visible or the user may not see
  // that section.
  // =============================================================================
  {
    name: 'preview.security',
    url: '/security',
    abstract: true,
    component: UIView,
    data: {
      title: 'Security',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },
  {
    name: 'preview.security.quarantine',
    // Filters, search, sort and page live in the URL. Multi-value filters are comma-separated. tab opens that tab
    // first (quarantine, cached-risk or containers); with tab=containers, back is the Containers list's sort and
    // page, so the container detail links back to the same page of the list.
    url: '/quarantine?tab&back&severity&policy&ecosystem&repository&q&sort&dir&page&pageSize',
    component: FirewallSecurityQuarantinePage,
    // dynamic: without it, every filter, search, sort or page change re-enters the state and remounts the page.
    params: {
      tab: { type: 'string', value: null, dynamic: true },
      back: { type: 'string', value: null, dynamic: true },
      severity: { type: 'string', value: null, dynamic: true },
      policy: { type: 'string', value: null, dynamic: true },
      ecosystem: { type: 'string', value: null, dynamic: true },
      repository: { type: 'string', value: null, dynamic: true },
      q: { type: 'string', value: null, dynamic: true },
      sort: { type: 'string', value: null, dynamic: true },
      dir: { type: 'string', value: null, dynamic: true },
      page: { type: 'string', value: null, dynamic: true },
      pageSize: { type: 'string', value: null, dynamic: true },
    },
    data: {
      title: 'Quarantine',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },
  {
    name: 'preview.security.quarantineDetail',
    // key is repositoryId:hash; ui-router encodes the ':' as %3A and decodes it back. back carries the list's query.
    url: '/quarantine/{key}?tab&back',
    component: FirewallSecurityQuarantineDetailPage,
    // dynamic: switching tab only changes the query, so the page is not remounted.
    params: {
      tab: { type: 'string', value: null, dynamic: true },
      back: { type: 'string', value: null, dynamic: true },
    },
    data: {
      title: 'Quarantine component',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },
  {
    name: 'preview.security.containerDetail',
    // key is the Containers tab's row key; back carries the list's sort and page for the breadcrumb.
    url: '/quarantine/containers/{key}?back',
    component: FirewallSecurityContainerDetailPage,
    data: {
      title: 'Container image',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },
  {
    name: 'preview.security.containerComponentDetail',
    // A component inside the image: container is the image's key, key the component's repositoryId:hash. back is the
    // Containers tab's sort and page, handed back to the image page.
    url: '/quarantine/containers/{container}/components/{key}?tab&back',
    component: FirewallSecurityContainerComponentDetailPage,
    params: {
      tab: { type: 'string', value: null, dynamic: true },
      back: { type: 'string', value: null, dynamic: true },
    },
    data: {
      title: 'Container component',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },
  {
    name: 'preview.security.quarantineViolation',
    // key is repositoryId:hash, as in quarantineDetail; violationId is the IQ policy violation id. back carries the
    // list's query so the breadcrumb returns to the component with the same list.
    url: '/quarantine/{key}/violations/{violationId}?back',
    component: FirewallSecurityQuarantineViolationPage,
    // dynamic: back only feeds the breadcrumb links, so a change in it does not remount the page.
    params: {
      back: { type: 'string', value: null, dynamic: true },
    },
    data: {
      title: 'Quarantine violation',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },
  {
    name: 'preview.security.waivers',
    // Tab, filters, search, sort and page live in the URL. Multi-value filters are comma-separated.
    url: '/waivers?type&view&status&threat&policy&repository&q&sort&dir&page&pageSize',
    component: FirewallSecurityWaiversPage,
    // dynamic: without it, every filter, search, sort or page change re-enters the state and remounts the page.
    params: {
      type: { type: 'string', value: null, dynamic: true },
      view: { type: 'string', value: null, dynamic: true },
      status: { type: 'string', value: null, dynamic: true },
      threat: { type: 'string', value: null, dynamic: true },
      policy: { type: 'string', value: null, dynamic: true },
      repository: { type: 'string', value: null, dynamic: true },
      q: { type: 'string', value: null, dynamic: true },
      sort: { type: 'string', value: null, dynamic: true },
      dir: { type: 'string', value: null, dynamic: true },
      page: { type: 'string', value: null, dynamic: true },
      pageSize: { type: 'string', value: null, dynamic: true },
    },
    data: {
      title: 'Waivers',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },
  {
    name: 'preview.security.waiverDetail',
    // back carries the list's query, so the back link returns to the same tab, filters and page.
    url: '/waivers/{waiverId}?back&type&view',
    component: FirewallSecurityWaiverDetailPage,
    data: {
      title: 'Waiver',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },
  {
    name: 'preview.security.waiverRenew',
    // Same params as the detail, so Cancel and Back return to it with the list's query intact.
    url: '/waivers/{waiverId}/renew?back&type&view',
    component: FirewallSecurityWaiverRenewPage,
    data: {
      title: 'Renew Waiver',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },
  {
    name: 'preview.security.malware',
    url: '/malware',
    component: FirewallSecurityMalwarePage,
    data: {
      title: 'Malware',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },
  {
    name: 'preview.security.evaluations',
    // Filters, search, sort and page live in the URL. Multi-value filters are comma-separated.
    url: '/evaluations?format&threat&violations&q&sort&dir&page&pageSize',
    component: FirewallSecurityEvaluationsPage,
    // dynamic: without it, every filter, search, sort or page change re-enters the state and remounts the page.
    params: {
      format: { type: 'string', value: null, dynamic: true },
      threat: { type: 'string', value: null, dynamic: true },
      violations: { type: 'string', value: null, dynamic: true },
      q: { type: 'string', value: null, dynamic: true },
      sort: { type: 'string', value: null, dynamic: true },
      dir: { type: 'string', value: null, dynamic: true },
      page: { type: 'string', value: null, dynamic: true },
      pageSize: { type: 'string', value: null, dynamic: true },
    },
    data: {
      title: 'Evaluations',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },
  {
    name: 'preview.security.policies',
    url: '/policies',
    component: FirewallSecurityPoliciesPage,
    data: {
      title: 'Policies',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },

  // =============================================================================
  // AUDIT LOG
  // Requires authentication to view
  // =============================================================================
  {
    name: 'preview.browse.audit',
    url: '/audit?repositoryName',
    component: AuditLogPage,
    params: {
      repositoryName: { type: 'string', dynamic: true, value: null },
    },
    data: {
      title: 'Audit',
      visibilityRequirements: {
        requiresUser: true,
        permissions: ['nexus:audit:read'],
        statesEnabled: [
          {
            key: 'previewAuditEnabled',
            defaultValue: false,
          },
        ],
      },
    },
  },

  // =============================================================================
  // MALICIOUS PACKAGES - Backward-compat redirect to Malware Risk
  // =============================================================================
  {
    name: 'preview.browse.malicious-packages',
    url: '/malicious-packages?tab',
    redirectTo: 'preview.browse.malwarerisk',
    params: {
      tab: {value: 'overview', type: 'string', dynamic: true},
    },
    data: {
      title: 'Malware Risk',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },

  // =============================================================================
  // MALWARE REMEDIATION (Legacy) - IMPLEMENTED ✅
  // Requires authentication to view
  // =============================================================================
  {
    name: 'preview.browse.remediate',
    url: '/remediate',
    component: RemediatePage,
    data: {
      title: 'Malicious Packages',
      visibilityRequirements: {
        requiresUser: true,
      },
    },
  },

  // =============================================================================
  // BROWSE - IMPLEMENTED ✅
  // =============================================================================
  {
    name: 'preview.browse.browse',
    url: '?format',
    component: BrowsePage,
    params: {
      format: { type: 'string', value: null, dynamic: true, inherit: true },
    },
    data: {
      title: 'Browse',
      // Mirrors Default UI BROWSE.BROWSE.ROOT (browseRoutes.js) and the public coreui
      // preview.browse.browse route: visible to users with any repository-view or
      // content-selector permission and at least one browseable format (NEXUS-54048).
      visibilityRequirements: {
        anonymousAccessOrHasUser: true,
        permissionPrefixes: ['nexus:repository-view', 'nexus:repository-content-selector'],
        statesEnabled: [{ key: 'browseableformats', defaultValue: [] }],
      },
    },
  },
  {
    name: 'preview.browse.browse.repo',
    url: '/{repoName:[^/]+}/',
    component: BrowsePage,
    params: {
      repoName: { value: null, dynamic: true },
      format: { type: 'string', value: null, dynamic: true, inherit: true },
    },
    data: {title: 'Browse Repository'},
  },
  {
    name: 'preview.browse.browse.repo.path',
    url: '*path?tab',
    component: BrowsePage,
    params: {
      repoName: { type: 'string', value: null, dynamic: true },
      path: { value: null, raw: true, dynamic: true },
      tab: { type: 'string', value: 'summary', dynamic: true }, // Default tab
      format: { type: 'string', value: null, dynamic: true, inherit: true },
    },
    data: {title: 'Browse'},
  },
  // Tab-based routing for asset detail views
  {
    name: 'preview.browse.browse.repo.path.summary',
    url: '/summary',
    component: BrowsePage,
    params: {
      repoName: { type: 'string', value: null, dynamic: true },
      path: { type: 'string', value: null, raw: true, dynamic: true },
      format: { type: 'string', value: null, dynamic: true, inherit: true },
      tab: { type: 'string', value: 'summary', dynamic: true },
    },
    data: {title: 'Summary'},
  },
  {
    name: 'preview.browse.browse.repo.path.usage',
    url: '/usage',
    component: BrowsePage,
    params: {
      repoName: { type: 'string', value: null, dynamic: true },
      path: { type: 'string', value: null, raw: true, dynamic: true },
      format: { type: 'string', value: null, dynamic: true, inherit: true },
      tab: { type: 'string', value: 'usage', dynamic: true },
    },
    data: {title: 'Usage'},
  },
  {
    name: 'preview.browse.browse.repo.path.attributes',
    url: '/attributes',
    component: BrowsePage,
    params: {
      repoName: { type: 'string', value: null, dynamic: true },
      path: { type: 'string', value: null, raw: true, dynamic: true },
      format: { type: 'string', value: null, dynamic: true, inherit: true },
      tab: { type: 'string', value: 'attributes', dynamic: true },
    },
    data: {title: 'Attributes'},
  },
  {
    name: 'preview.browse.browse.repo.path.tags',
    url: '/tags',
    component: BrowsePage,
    params: {
      repoName: { type: 'string', value: null, dynamic: true },
      path: { type: 'string', value: null, raw: true, dynamic: true },
      format: { type: 'string', value: null, dynamic: true, inherit: true },
      tab: { type: 'string', value: 'tags', dynamic: true },
    },
    data: {title: 'Component Tags'},
  },
  {
    name: 'preview.browse.browse.repo.path.lifecycle',
    url: '/lifecycle',
    component: BrowsePage,
    params: {
      repoName: { type: 'string', value: null, dynamic: true },
      path: { type: 'string', value: null, raw: true, dynamic: true },
      format: { type: 'string', value: null, dynamic: true, inherit: true },
      tab: { type: 'string', value: 'lifecycle', dynamic: true },
    },
    data: {title: 'Sonatype Lifecycle'},
  },

  // =============================================================================
  // UPLOAD - IMPLEMENTED ✅
  // =============================================================================
  // Base upload route - abstract container
  {
    name: 'preview.browse.upload',
    url: '/upload',
    abstract: true,
    component: UIView,
    data: {
      title: 'Upload',
      // Mirrors Default UI BROWSE.UPLOAD.ROOT (browseRoutes.js) and the public coreui
      // preview.browse.upload route (NEXUS-54048).
      visibilityRequirements: {
        permissions: [Permissions.COMPONENT.CREATE],
      },
    },
  },
  // Upload list (repository selection)
  {
    name: 'preview.browse.upload.list',
    url: '',
    component: UploadPage,
    data: {title: 'Upload'},
  },
  // Upload form for a specific repository
  {
    name: 'preview.browse.upload.form',
    url: '/:repoName',
    component: UploadFormPage,
    params: {
      repoName: { type: 'string', value: null, dynamic: true },
    },
    data: {title: 'Upload to Repository'},
  },
  // Alias for compatibility with left nav which uses browse.upload.edit -> preview.browse.upload.edit
  {
    name: 'preview.browse.upload.edit',
    redirectTo: 'preview.browse.upload.form',
  },

  // =============================================================================
  // TAGS - IMPLEMENTED ✅
  // =============================================================================
  {
    name: 'preview.browse.tags',
    url: '/tags',
    component: TagsPage,
    data: {
      title: 'Tags',
      // Mirrors Default UI BROWSE.TAGS.ROOT (browseRoutes.js) and the public coreui
      // preview.browse.tags route. Without this the Cloud route was ungated, so Tags
      // leaked into the left nav and direct URL access returned a 403 instead of
      // redirecting to the dashboard (NEXUS-54048).
      visibilityRequirements: {
        permissions: [Permissions.TAGS.READ],
        editions: PRO_FAMILY_EDITIONS,
      },
    },
  },
  {
    name: 'preview.browse.tagdetail',
    url: '/tags/:tagName',
    component: TagDetailPage,
    params: {
      tagName: { type: 'string', value: null, dynamic: true },
    },
    data: {
      title: 'Tag Details',
      // Tag detail is a sibling of preview.browse.tags (its UIRouter parent is
      // preview.browse, which is ungated), so it does not inherit the tags gate via the
      // guard's ancestor walk. Gate it explicitly so a direct URL to a tag detail
      // redirects to the dashboard instead of loading and failing with a 403 (NEXUS-54048).
      visibilityRequirements: {
        permissions: [Permissions.TAGS.READ],
        editions: PRO_FAMILY_EDITIONS,
      },
    },
  },
  {
    name: 'preview.browse.trash',
    url: '/trash',
    component: TrashPage,
    data: {
      title: 'Trash',
      visibilityRequirements: {
        statesEnabled: [{ key: 'nexus.trash.enabled', defaultValue: false }],
        permissions: [Permissions.ADMIN],
        editions: PRO_FAMILY_EDITIONS,
      },
    },
  },

  // =============================================================================
  // HEALTH CHECK + FIREWALL REPORT PAGES - Dedicated full-page reports
  // Replaces cramped SecurityReportModal; iframe fills viewport
  // =============================================================================
  {
    name: 'preview.browse.health-report',
    url: '/health-report/:repositoryName',
    component: HealthReportPage,
    params: {
      repositoryName: { type: 'string', dynamic: true },
    },
    data: { title: 'Health Check Report' },
  },
  {
    name: 'preview.browse.firewall-report',
    url: '/firewall-report/:repositoryName',
    component: FirewallReportPage,
    params: {
      repositoryName: { type: 'string', dynamic: true },
    },
    data: { title: 'Firewall Report' },
  },

  // =============================================================================
  // MALWARE RISK — Primary Nexus One UI page (1:1 with Default UI Malware Risk)
  // =============================================================================
  {
    name: 'preview.browse.malwarerisk',
    url: '/malwarerisk',
    component: MalwareRiskPageSuper,
    data: {
      title: 'Malware Risk',
      visibilityRequirements: {
        permissions: [Permissions.ADMIN],
      },
    },
  },
  // Backward compat: old URL redirects to Malware Risk
  {
    name: 'preview.browse.malware-defense',
    url: '/malware-defense',
    redirectTo: 'preview.browse.malwarerisk',
    data: { title: 'Malware Risk' },
  },

  // =============================================================================
  // SEARCH PARENT
  // =============================================================================
  {
    name: 'preview.browse.search',
    url: '/search',
    abstract: true,
    component: UIView,
    data: {
      title: 'Search',
      visibilityRequirements: {
        permissions: ['nexus:search:read'],
      },
    },
  },
  
  // =============================================================================
  // UNIFIED SEARCH PAGE - Main entry point for ALL formats
  // =============================================================================
  {
    name: 'preview.browse.search.unified',
    url: '?q&format&sort&direction',
    component: UnifiedSearchPage,
    params: {
      q: { value: null, dynamic: true },
      format: { value: null, dynamic: true },
      sort: { value: null, dynamic: true },
      direction: { value: null, dynamic: true },
    },
    data: {title: 'Search Components'},
  },

  // =============================================================================
  // CUSTOM SEARCH
  // =============================================================================
  {
    name: 'preview.browse.search.custom',
    url: '/custom',
    component: CustomSearchPage,
    data: {title: 'Custom Search'},
  },

  // =============================================================================
  // COMPONENT DETAIL
  // =============================================================================
  {
    name: 'preview.browse.search.component',
    url: '/component/:gaId?version',
    component: GADetailPage,
    params: {
      // 'path' (/[^/]*/), not 'string' (/.*/): the default string pattern matches across `/`, so
      // a deep link to a tab absorbed the segment and gaId became 'npm:dummy:name/overview'.
      // Leaving `raw` off keeps encoding symmetric — the router encodes on write and decodes on
      // read, so callers must pass the plain 'format:group:name' and never pre-encode it. A
      // pre-encoded value cannot round-trip, and because gaId is not `dynamic`, the mismatch
      // costs a state re-entry that remounts the page and re-fires every request (NEXUS-54201).
      gaId: { type: 'path' },
      // dynamic: without it, changing `version` re-enters the state and remounts the page.
      version: { type: 'string', value: null, squash: true, dynamic: true },
    },
    resolve: [
      { token: 'gaId', deps: ['$stateParams'], resolveFn: ($stateParams) => $stateParams.gaId },
      { token: 'version', deps: ['$stateParams'], resolveFn: ($stateParams) => $stateParams.version },
    ],
    data: {title: 'Component Details'},
  },
  {
    name: 'preview.browse.search.component.overview',
    url: '/overview',
    component: GADetailPage,
    resolve: [
      { token: 'gaId', deps: ['$stateParams'], resolveFn: ($stateParams) => $stateParams.gaId },
      { token: 'version', deps: ['$stateParams'], resolveFn: ($stateParams) => $stateParams.version },
    ],
    data: {title: 'Overview'},
  },
  {
    name: 'preview.browse.search.component.versions',
    url: '/versions',
    component: GADetailPage,
    resolve: [
      { token: 'gaId', deps: ['$stateParams'], resolveFn: ($stateParams) => $stateParams.gaId },
      { token: 'version', deps: ['$stateParams'], resolveFn: ($stateParams) => $stateParams.version },
    ],
    data: {title: 'Versions'},
  },
  {
    name: 'preview.browse.search.component.repos',
    url: '/repos',
    component: GADetailPage,
    resolve: [
      { token: 'gaId', deps: ['$stateParams'], resolveFn: ($stateParams) => $stateParams.gaId },
      { token: 'version', deps: ['$stateParams'], resolveFn: ($stateParams) => $stateParams.version },
    ],
    data: {title: 'Repositories'},
  },
  {
    name: 'preview.browse.search.component.files',
    url: '/files',
    component: GADetailPage,
    resolve: [
      { token: 'gaId', deps: ['$stateParams'], resolveFn: ($stateParams) => $stateParams.gaId },
      { token: 'version', deps: ['$stateParams'], resolveFn: ($stateParams) => $stateParams.version },
    ],
    data: {title: 'Files'},
  },
  {
    name: 'preview.browse.search.component.security',
    url: '/security',
    component: GADetailPage,
    resolve: [
      { token: 'gaId', deps: ['$stateParams'], resolveFn: ($stateParams) => $stateParams.gaId },
      { token: 'version', deps: ['$stateParams'], resolveFn: ($stateParams) => $stateParams.version },
    ],
    data: {title: 'Security'},
  },
];
